// A NOTE IN AN AI ANSWER — the `note-view` view the assistant mounts in its
// thread and a Fly page places as a section (workspace functions/
// assistantAppViews.js, the `views[]` contract).
//
// documentsAiViews() is the PROVIDER, read from the shell's eager documents
// slice; the view's component rides the lazy documents context (`fold:
// 'documents'` — the shell folds it through ensureDocumentsContext before a
// card mounts). One view, in the shell's list frame (workspace components/
// AssistantViewList.js):
//
//   note-view   one note or knowledge-base article, read-only: its title row
//               (author · updated; it opens the note in Notes) over its words
//               (the shared markdown renderer). `note` = words in the title
//               (the body when no title matches), empty = the newest;
//               `space` = notes, the knowledge base, or both.
//
// The reads are the Notes pages' own (`docs`, `docs.kbArticles`, list — SDK
// only), into the view's OWN state — never root.documents / root.kbArticles.
// Nothing here writes.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function documentsAiViews() {
  return {
    app: {
      id: 'system/documents',
      navbar: [{ label: 'shell.app.notes', route: '/notes', icon: 'document' }],
      apps: [{ icon: 'document' }]
    },
    fold: 'documents',
    views: [
      {
        id: 'note-view',
        title: 'documents.answer.title',
        describe:
          'One note or knowledge-base article, read-only, with its words; note = words in its title (empty = the newest); space = notes, the knowledge base, or both.',
        component: 'NoteAnswerView',
        icon: 'document',
        hue: 'documents',
        size: 'l',
        params: {
          note: {
            type: 'string',
            describe: 'words in the note or article title; empty = the newest'
          },
          space: {
            type: 'enum',
            values: ['all', 'notes', 'kb'],
            labels: {
              all: 'documents.answer.space.all',
              notes: 'documents.answer.space.notes',
              kb: 'documents.answer.space.kb'
            },
            default: 'all'
          }
        },
        link: { route: '/notes', query: {} },
        access: { route: '/notes' }
      }
    ]
  }
}

// The read: the notes and/or the articles, the best match for `note` (the
// title first, then the words), else the newest. `{ data: { doc, matches },
// failed }` — `doc` null when nothing matches.
export async function noteAnswerLoad(el, params) {
  const host = el || this
  const p = params && typeof params === 'object' ? params : {}
  const sdk = (host && host.sdk) || host.call('getSDK')
  if (!sdk || typeof sdk.execute !== 'function')
    return { data: { doc: null, matches: 0 }, failed: true }
  const space = p.space === 'notes' || p.space === 'kb' ? p.space : 'all'
  const rowsOf = (res) =>
    Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : []
  let failed = false
  const [docsRes, kbRes] = await Promise.all([
    space === 'kb'
      ? []
      : Promise.resolve()
          .then(() => sdk.execute('docs', 'list', {}))
          .catch(() => {
            failed = true
            return []
          }),
    space === 'notes'
      ? []
      : Promise.resolve()
          .then(() => sdk.execute('docs.kbArticles', 'list', {}))
          .catch(() => {
            if (space === 'kb') failed = true
            return []
          })
  ])
  const shape = (r, kind) => ({
    id: String(r.docId || r._id || r.id || ''),
    kind,
    title: r.title || '',
    author: r.author || '',
    at: Date.parse(r.updatedAt || r.createdAt || '') || 0,
    content: host.call('_parseBody', r.body || r.content)
  })
  // The /notes grid's own rule: KB articles live in their own store, a doc
  // moved to the files desktop lives there.
  const notes = rowsOf(docsRes)
    .filter((r) => r && r.type !== 'kb_article' && !(r.metadata && r.metadata.movedToFiles))
    .map((r) => shape(r, 'note'))
  const articles = rowsOf(kbRes)
    .filter(Boolean)
    .map((r) => shape(r, 'kb'))
  const all = notes.concat(articles).filter((x) => x.id)
  const ask = String(p.note || '')
    .trim()
    .toLowerCase()
  const words = ask.split(/\s+/).filter(Boolean)
  const textOf = (x) =>
    typeof x.content === 'string' ? x.content : String(host.call('_joinBodyText', x.content) || '')
  const score = (x) => {
    const title = x.title.toLowerCase()
    if (title && title === ask) return 3
    if (words.every((w) => title.includes(w))) return 2
    const body = textOf(x).toLowerCase()
    return words.every((w) => body.includes(w)) ? 1 : 0
  }
  const ranked = words.length
    ? all
        .map((x) => ({ x, s: score(x) }))
        .filter((r) => r.s > 0)
        .sort((a, b) => b.s - a.s || b.x.at - a.x.at)
        .map((r) => r.x)
    : all.slice().sort((a, b) => b.at - a.at)
  const doc = ranked[0] || null
  return {
    data: { doc, matches: ranked.length },
    failed: failed && !all.length
  }
}

// The model: the note's title row (author or the knowledge base · updated),
// then its words as markdown — a legacy blocks body through the editor's own
// converter.
export function noteAnswerModel(data, params, failed) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const say = (key) => String(el.call('polyglot', key))
  const doc = d.doc
  if (!doc)
    return { groups: [], note: failed ? 'documents.answer.failed' : 'documents.answer.empty' }
  let body = ''
  if (typeof doc.content === 'string') body = doc.content
  else if (doc.content && typeof doc.content === 'object') {
    try {
      body = String(el.call('_toMarkdown', doc.content) || '')
    } catch (_) {
      body = ''
    }
    if (!body) body = String(el.call('_joinBodyText', doc.content) || '')
  }
  const people = el.call('peopleByEmail', root) || {}
  const author = doc.author ? (people[doc.author] && people[doc.author].name) || doc.author : ''
  const updated = doc.at
    ? String(el.call('localizedDate', new Date(doc.at), { month: 'short', day: 'numeric' }) || '')
    : ''
  const kb = doc.kind === 'kb'
  const row = el.call('assistantViewRow', {
    key: 'd-' + String(doc.id).replace(/[^a-zA-Z0-9_-]/g, ''),
    title: doc.title || say(kb ? 'documents.kb.untitledArticle' : 'documents.notes.untitled'),
    meta: [kb ? say('documents.answer.space.kb') : author, updated].filter(Boolean).join(' · '),
    icon: 'document',
    open: {
      call: 'openAppPage',
      arg: (kb ? '/notes/knowledge-base?id=' : '/notes?id=') + encodeURIComponent(doc.id)
    }
  })
  return {
    groups: [el.call('assistantViewGroup', { key: 'note', rows: [row] })],
    body: body.trim(),
    note: body.trim() ? '' : 'documents.notes.emptyBody',
    more: Number(d.matches) > 1 ? { shown: 1, total: Number(d.matches) } : null
  }
}
